// In src/Drivers.js

import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db, auth } from './firebase';
import { FaEdit, FaSave, FaTrash, FaPaperPlane, FaQrcode } from 'react-icons/fa';

const EditDriverModal = ({ driver, onClose, onSaveProfile, onUpdateStatus, isProcessing }) => {
  const [editedDriver, setEditedDriver] = useState(driver);

  useEffect(() => {
    setEditedDriver(driver);
  }, [driver]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedDriver(prev => ({ ...prev, [name]: value }));
  };

  const handleHighlightChange = (index, value) => {
    const newHighlights = [...editedDriver.highlights];
    newHighlights[index] = value;
    setEditedDriver(prev => ({ ...prev, highlights: newHighlights }));
  };
  
  const handleRemoveHighlight = (indexToClear) => {
    // Create a copy of the highlights array
    const newHighlights = [...editedDriver.highlights];
    
    // Set the specific highlight to be an empty string
    newHighlights[indexToClear] = '';
    
    // Update the state with the new array
    setEditedDriver(prev => ({ ...prev, highlights: newHighlights }));
  };
   const handleGenerateQr = async (qrType) => {
    // Determine which URL to encode based on the button clicked
    const urlToEncode = qrType === 'cashApp' ? editedDriver.cashAppQR : editedDriver.venmoQR;
    if (!urlToEncode || !urlToEncode.startsWith('http')) {
      alert('Please enter a valid URL first (e.g., https://cash.app/$YourName)');
      return;
    }

    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken(true);

      // This calls the 'generateQrCode' function you created
      const result = await fetch("https://us-central1-drivio-dashboard-40e1f.cloudfunctions.net/generateQrCode", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        // V1 onCall functions expect the payload to be wrapped in a 'data' object
        body: JSON.stringify({ url: urlToEncode }),
      });

      if (!result.ok) {
        const errorData = await result.json();
        throw new Error(errorData.error.message || 'Failed to generate QR code.');
      }

      // Get the new QR code image URL from the response
     const data = await result.json();
const { qrCodeUrl } = data;

      // Update the correct state variable with the new image URL
      if (qrType === 'cashApp') {
        setEditedDriver(prev => ({ ...prev, cashAppQR: qrCodeUrl }));
      } else {
        setEditedDriver(prev => ({ ...prev, venmoQR: qrCodeUrl }));
      }

    } catch (err) {
      alert(`Error generating QR Code: ${err.message}`);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{maxWidth: '700px'}}>
        <div className="modal-header">
          <h3>Edit Driver: {driver.firstName} {driver.lastName}</h3>
          <button onClick={onClose} className="modal-close-btn">&times;</button>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div className="detail-item">
    <label>Activation Code</label>
    <input 
      type="text" 
      value={driver.activationCode || 'N/A'} 
      className="form-input" 
      readOnly 
      disabled 
    />
  </div>
            <div className="detail-item">
              <label>First Name</label>
              <input type="text" name="firstName" value={editedDriver.firstName} onChange={handleInputChange} className="form-input"/>
            </div>
            <div className="detail-item">
              <label>Last Name</label>
              <input type="text" name="lastName" value={editedDriver.lastName} onChange={handleInputChange} className="form-input"/>
            </div>
           <div className="detail-item">
  <label>Cash App URL / QR Image URL</label>
  <div className="input-with-button">
    <input type="text" name="cashAppQR" value={editedDriver.cashAppQR} onChange={handleInputChange} className="form-input"/>
    <button onClick={() => handleGenerateQr('cashApp')} className="btn btn-secondary btn-small">
        <FaQrcode /> Generate
    </button>
  </div>
</div>

{/* ✅ UPDATED: Venmo input with Generate button */}
<div className="detail-item">
  <label>Venmo URL / QR Image URL</label>
  <div className="input-with-button">
    <input type="text" name="venmoQR" value={editedDriver.venmoQR} onChange={handleInputChange} className="form-input"/>
    <button onClick={() => handleGenerateQr('venmo')} className="btn btn-secondary btn-small">
        <FaQrcode /> Generate
    </button>
  </div>
</div>
          </div>
          <div className="content-editor">
            <h4>Highlights (3 max)</h4>
            {editedDriver.highlights.map((highlight, index) => (
              <div key={index} className="highlight-edit-item">
                <input type="text" value={highlight} onChange={(e) => handleHighlightChange(index, e.target.value)} className="form-input"/>
                <button onClick={() => handleRemoveHighlight(index)} className="btn btn-decline btn-small"><FaTrash /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer" style={{justifyContent: 'space-between'}}>
          <div>
            {driver.status === 'active' && (
              <>
                <button onClick={() => onUpdateStatus(driver.id, 'paused')} className="btn btn-secondary" disabled={isProcessing}>Pause Driver</button>
                <button onClick={() => onUpdateStatus(driver.id, 'deactivated')} className="btn btn-decline" disabled={isProcessing}>Deactivate Driver</button>
              </>
            )}
            {(driver.status === 'paused' || driver.status === 'deactivated') && (
              <button onClick={() => onUpdateStatus(driver.id, 'active')} className="btn btn-approve" disabled={isProcessing}>Reactivate Driver</button>
            )}
          </div>
          <div>
            <button onClick={onClose} className="btn btn-secondary" disabled={isProcessing}>Cancel</button>
            <button onClick={() => onSaveProfile(driver.id, editedDriver)} className="btn btn-primary" disabled={isProcessing}>
              <FaSave /> Save Profile Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('active');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
  setLoading(true);

  // ✅ NEW: Dynamically choose which collection to query
  let collectionName = 'drivers';
  let statusToQuery = filter;

  if (filter === 'pending_activation') {
    collectionName = 'pending_activations';
    statusToQuery = 'pending'; // In this collection, the status is always 'pending'
  }

  const dataRef = collection(db, collectionName);
  const q = query(dataRef, where("status", "==", statusToQuery));

  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const data = [];
    querySnapshot.forEach((doc) => data.push({ id: doc.id, ...doc.data() }));
    setDrivers(data);
    setLoading(false);
  }, (err) => {
    setError(`Failed to load data for ${filter}.`);
    setLoading(false);
  });

  return () => unsubscribe();
}, [filter]); // This hook now correctly depends on the 'filter' state
 const handleSendReminder = async (pendingActivationId) => {
    setIsProcessing(true);
    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken(true);
      const result = await fetch("https://us-central1-drivio-dashboard-40e1f.cloudfunctions.net/sendActivationReminder", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({ pendingActivationId }),
      });
      if (!result.ok) throw new Error("Failed to send reminder.");
      alert("Reminder email sent successfully!");
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleUpdateStatus = async (driverId, newStatus) => {
    if (!window.confirm(`Are you sure you want to set this driver to '${newStatus}'?`)) return;
    setIsProcessing(true);
    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken(true);
      const result = await fetch("https://us-central1-drivio-dashboard-40e1f.cloudfunctions.net/updateDriverStatus", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({ driverId, newStatus }),
      });
      if (!result.ok) throw new Error("Failed to update status.");
      alert("Driver status updated!");
      setSelectedDriver(null);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveProfile = async (driverId, profileData) => {
    setIsProcessing(true);
    // Sanitize data before sending
    const { firstName, lastName, cashAppQR, venmoQR, highlights } = profileData;
    const dataToSend = { firstName, lastName, cashAppQR, venmoQR, highlights };
    try {
      const user = auth.currentUser;
      const idToken = await user.getIdToken(true);
      const result = await fetch("https://us-central1-drivio-dashboard-40e1f.cloudfunctions.net/updateDriverProfile", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({ driverId, profileData: dataToSend }),
      });
      if (!result.ok) throw new Error("Failed to save profile.");
      alert("Driver profile saved!");
      setSelectedDriver(null);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="card">
      {selectedDriver && (
        <EditDriverModal 
          driver={selectedDriver}
          onClose={() => setSelectedDriver(null)}
          onUpdateStatus={handleUpdateStatus}
          onSaveProfile={handleSaveProfile}
          isProcessing={isProcessing}
        />
      )}
      <h2 className="page-header">Driver Management</h2>
<div className="filter-tabs">
  <button className={`filter-tab ${filter === 'active' ? 'active' : ''}`} onClick={() => setFilter('active')}>Active</button>
  {/* ✅ ADDED THIS NEW TAB */}
  <button className={`filter-tab ${filter === 'pending_activation' ? 'active' : ''}`} onClick={() => setFilter('pending_activation')}>Pending Activation</button>
  <button className={`filter-tab ${filter === 'paused' ? 'active' : ''}`} onClick={() => setFilter('paused')}>Paused</button>
  <button className={`filter-tab ${filter === 'deactivated' ? 'active' : ''}`} onClick={() => setFilter('deactivated')}>Deactivated</button>
</div>
      {loading ? <p>Loading drivers...</p> : error ? <p className="error-text">{error}</p> : (
       <table className="table">
  <thead>
    <tr>
      <th>Name</th>
      <th>Email</th>
      {filter === 'pending_activation' && <th>Activation Code</th>}
      <th>{filter === 'pending_activation' ? 'Approved At' : 'Tablet ID'}</th>
      {filter === 'pending_activation' && (
        <th>Last Reminder Sent</th>
      )}
      <th>Actions</th>
    </tr>
  </thead>
  <tbody>
    {drivers.map(driver => (
      <tr key={driver.id}>
        <td>{driver.firstName} {driver.lastName}</td>
        <td>{driver.email}</td>
    {filter === 'pending_activation' && <td><strong>{driver.activationCode}</strong></td>}
        <td>
          {filter === 'pending_activation' 
            ? driver.approvedAt?.toDate().toLocaleString() 
            : (driver.tabletId ? `${driver.tabletId.substring(0, 8)}...` : 'N/A')
          }
        </td>
        {filter === 'pending_activation' && (
          <td>
            {driver.lastReminderSentAt 
              ? driver.lastReminderSentAt.toDate().toLocaleString() 
              : 'Never'
            }
          </td>
        )}
        {/* ✅ Conditional Action Buttons */}
        <td>
          {filter === 'pending_activation' ? (
            <button onClick={() => handleSendReminder(driver.id)} className="btn btn-primary" disabled={isProcessing}>
              <FaPaperPlane /> {isProcessing ? '...' : 'Send Reminder'}
            </button>
          ) : (
            <button onClick={() => setSelectedDriver(driver)} className="btn btn-view">
              <FaEdit /> Edit Profile
            </button>
          )}
        </td>
      </tr>
    ))}
  </tbody>
</table>
      )}
    </div>
  );
}

export default Drivers;